import {
  Colors,
  Flex,
  HorizontalSpacing,
  IconChevronRight,
  IconInfo,
  Tag,
  Typography,
} from '@banhala/ui-kit';
import type { MarketDetail } from 'shared/src/api';
import { MarketProfileImage } from './MarketProfileImage';
import type { MumulLiveStatusDisplay } from '../constants/mumulLiveStatusDisplay';

type Market = MarketDetail['market'];

type Props = {
  market: Market;
  /** 무물라이브 상태 표시 정보. null 이면 기본(DEFAULT) 레이아웃. */
  statusDisplay: MumulLiveStatusDisplay | null;
  /** UNREAD 스토리 여부. true 면 프로필 링 진입 애니메이션 재생. */
  isUnread: boolean;
  /** 프로필 콘텐츠 보유 여부. true 면 콘텐츠 더보기 노출. */
  hasContents: boolean;
  /** 프로필 이미지 클릭 (상태별 액션). */
  onPressProfile: () => void;
  /** 마지막 확인 이후 새 콘텐츠 존재 여부. true 면 콘텐츠 더보기 좌측에 NEW 뱃지 노출. */
  hasNewContents: boolean;
  /** 콘텐츠 더보기 클릭 (무물라이브 콘텐츠 모아보기 바텀시트). */
  onPressMoreContents: () => void;
  /** 마켓명 옆 마켓정보 클릭. */
  onPressMarketInfo: () => void;
};

/** 콘텐츠 더보기 버튼 내 NEW 뱃지. */
const NewBadge = () => (
  <Flex.Center
    backgroundColor={'pink5'}
    borderRadius={'xsmall'}
    padding={{ horizontal: 2, vertical: 1 }}
  >
    <Typography type={'subtitle7'} color={'content_pink_primary'}>
      NEW
    </Typography>
  </Flex.Center>
);

/** 마켓명 옆 마켓정보 진입점. */
const MarketInfoButton = ({ onPress }: { onPress: () => void }) => (
  <Flex.Center width={20} height={20} onPress={onPress} testID={'마켓정보버튼'}>
    <IconInfo width={16} height={16} fill={Colors.content_secondary} />
  </Flex.Center>
);

/** 공식 / 마켓 타입 태그 묶음. */
const MarketTags = ({ market }: { market: Market }) => (
  <>
    {market.is_official && (
      <Tag color="blue" size="xsmall" type="secondary">
        공식
      </Tag>
    )}
    <Tag color="gray" size="xsmall" type="secondary">
      {market.market_type.name}
    </Tag>
  </>
);

/**
 * 마켓홈 상단 프로필 헤더.
 * - 무물라이브 상태가 있으면: 마켓명 + 태그를 한 줄(row)로, 아래에 상태 텍스트
 * - 기본(DEFAULT): 태그가 위, 마켓명이 아래(column)
 */
export const MarketProfileHeader = ({
  market,
  statusDisplay,
  isUnread,
  hasContents,
  hasNewContents,
  onPressProfile,
  onPressMoreContents,
  onPressMarketInfo,
}: Props) => {
  return (
    <Flex.Row padding={{ vertical: 2 }} alignItems={'center'}>
      <MarketProfileImage
        uri={market.image}
        ringColor={statusDisplay?.ringColor ?? null}
        animated={isUnread}
        onPress={onPressProfile}
      />
      <HorizontalSpacing size={10} />
      <Flex flex={1} gap={4}>
        <Flex.Row gap={8} alignItems={'center'}>
          <MarketTags market={market} />
        </Flex.Row>
        <Flex.Row gap={2} alignItems={'center'}>
          <Typography type={'h5'} numberOfLines={1}>
            {market.name}
          </Typography>
          {!market.is_exited && (
            <MarketInfoButton onPress={onPressMarketInfo} />
          )}
        </Flex.Row>
      </Flex>
      {hasContents && (
        <MoreContentsButton
          hasNewContents={hasNewContents}
          onPress={onPressMoreContents}
        />
      )}
    </Flex.Row>
  );
};

/** 콘텐츠 더보기 진입점. */
const MoreContentsButton = ({
  hasNewContents,
  onPress,
}: {
  hasNewContents: boolean;
  onPress: () => void;
}) => (
  <Flex.Row
    gap={2}
    alignItems={'center'}
    borderWidth={1}
    borderColor={'border_tertiary'}
    borderRadius={'small'}
    padding={{ left: 10, right: 8, vertical: 6 }}
    onPress={onPress}
    testID={'콘텐츠더보기버튼'}
  >
    <Flex.Row alignItems={'center'}>
      {hasNewContents && (
        <>
          <NewBadge />
          <HorizontalSpacing size={6} />
        </>
      )}
      <Typography type={'body4'} color={'content_primary'}>
        콘텐츠 더보기
      </Typography>
    </Flex.Row>
    <IconChevronRight width={12} height={12} fill={Colors.content_primary} />
  </Flex.Row>
);
